import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadStateApiService } from '../services/lead-state-api.service';
import { LeadStateResponse } from '../models/lead-state.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-lead-state-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    DragDropModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    HasPermissionDirective
  ],
  templateUrl: './lead-state-list.component.html',
  styleUrls: ['./lead-state-list.component.css']
})
export class LeadStateListComponent implements OnInit {
  estados: LeadStateResponse[] = [];
  loading = false;
  error = '';
  /** Reorden en vuelo: evita drags encadenados mientras el POST no confirma. */
  reordering = false;

  constructor(
    private stateApi: LeadStateApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService
  ) { }

  /**
   * `POST /api/lead-states/reorder` exige `settings:EDIT` (LeadStateController.MODULE = "settings"),
   * no `leads:edit` como los botones Editar/Eliminar de esta misma tabla. Ese desajuste es
   * preexistente y afecta a los botones, no al arrastre: aquí se consulta el permiso real del endpoint.
   */
  get canReorder(): boolean {
    return this.permissions.has('settings', 'edit');
  }

  ngOnInit(): void {
    this.cargarEstados();
  }

  cargarEstados(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.stateApi.getStates(businessId).subscribe({
      next: (data) => {
        this.estados = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los tipos de lead';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  /**
   * Reordena por drag & drop. La lista no pagina (size=100), así que `estados` es SIEMPRE
   * la lista completa del negocio y se puede enviar entera al endpoint de reorden.
   *
   * Estrategia optimista: se mueve la fila y se renumera la columna "Orden" (1..N) al instante;
   * si la API falla se revierte el orden y el displayOrder exactos que había antes.
   * Sin toast en el éxito (regla de drag-drop del skill crm-toast-notifications).
   */
  dropEstado(event: CdkDragDrop<LeadStateResponse[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (!this.canReorder || this.reordering) return;

    // Snapshot para revertir: conserva posición Y displayOrder previos de cada ítem.
    const snapshot = this.estados.map(e => ({ item: e, displayOrder: e.displayOrder }));

    moveItemInArray(this.estados, event.previousIndex, event.currentIndex);
    this.estados.forEach((e, idx) => e.displayOrder = idx + 1);

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const orderedIds = this.estados.map(e => e.id);

    this.reordering = true;
    this.stateApi.reorderStates(businessId, orderedIds).subscribe({
      next: () => {
        this.reordering = false;
      },
      error: (err) => {
        this.reordering = false;
        // Revertir: la UI no puede quedar mostrando un orden que el backend rechazó.
        this.estados = snapshot.map(s => {
          s.item.displayOrder = s.displayOrder;
          return s.item;
        });
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al reordenar los tipos de lead'
        });
      }
    });
  }

  nuevoEstado(): void {
    this.router.navigate(['/config-leads/tipos/crear']);
  }

  editarEstado(estado: LeadStateResponse): void {
    this.router.navigate(['/config-leads/tipos/editar', estado.id]);
  }

  async eliminarEstado(estado: LeadStateResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tipo',
      message: `¿Está seguro de eliminar el tipo "${estado.name}"?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.stateApi.deleteState(estado.id, businessId).subscribe({
      next: () => {
        this.cargarEstados();
        this.toast.deleted('Tipo de lead', `Se eliminó "${estado.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el tipo' });
      }
    });
  }

  getActiveColor(isActive: boolean): BadgeColor {
    return isActive ? 'success' : 'error';
  }

  getActiveLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }
}
